/**
 * A busca por município (pedido do usuário, 08/10/2026): escolher um município
 * (pelo nome ou pela localização) destaca e enquadra ele no mapa.
 *
 * - Desktop: o campo sempre à vista no meio do topo do mapa (09/10/2026; sem
 *   lugar, o primeiro das ações, ui/MapActions); a lista e a localização abrem num cartão abaixo dele. "/" ou
 *   Ctrl/⌘+K levam a ele. Escolhido, o nome fica no campo, com um ✕.
 * - Celular: "Pesquisar" ao lado do relógio, no topo (só a lupa abaixo de
 *   360 px); o campo abre numa folha no topo da tela. Escolhido, o botão vira
 *   o nome dele.
 *
 * A localização é só por toque em "Usar minha localização": o navegador pede a
 * permissão nessa hora, nunca ao abrir o site.
 */
import clsx from 'clsx';
import { LoaderCircle, LocateFixed, MapPin, Search, X } from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useRef, useState, type ChangeEvent, type FocusEvent, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { useModalLayer } from '../ui/modalStack';
import { CHIP } from '../ui/MapActions';
import { buscarMunicipios, escolherMunicipio, getLoadedAreaIndex, loadAreaIndex, localizar, MENSAGEM_DO_GPS } from './municipios';
import { useBuscaStore, type MunicipioEscolhido } from './store';
import type { AreaIndex } from '../fires/areaSearch';

/** O índice dos nomes: baixado na primeira vez que o campo abre. */
function useIndice(pedir: boolean): { indice: AreaIndex | null; erro: boolean } {
  const [indice, setIndice] = useState<AreaIndex | null>(() => getLoadedAreaIndex());
  const [erro, setErro] = useState(false);
  useEffect(() => {
    if (!pedir || indice) return;
    let viva = true;
    loadAreaIndex()
      .then((i) => viva && setIndice(i))
      .catch(() => viva && setErro(true));
    return () => {
      viva = false;
    };
  }, [pedir, indice]);
  return { indice, erro };
}

const rotulo = (m: MunicipioEscolhido) => (m.uf ? `${m.nome} – ${m.uf}` : m.nome || 'Município');

/** O texto digitado, a lista de resultados e as teclas (o mesmo na folha do celular e na barra do desktop). */
function useCampo(opts: { aberta: boolean; abrir?: () => void; aoEscolher: () => void }) {
  const [texto, setTexto] = useState('');
  const [ativo, setAtivo] = useState(0);
  const { indice, erro } = useIndice(opts.aberta);
  const lista = useId();
  const resultados = useMemo(() => (indice && texto.trim() ? buscarMunicipios(indice, texto) : []), [indice, texto]);
  const expandida = opts.aberta && resultados.length > 0;
  useEffect(() => setAtivo(0), [texto]);
  // Nas setas, a lista rola até a opção ativa.
  const idAtivo = expandida && resultados[ativo] ? `${lista}-${resultados[ativo].cd}` : undefined;
  useEffect(() => {
    if (idAtivo) document.getElementById(idAtivo)?.scrollIntoView({ block: 'nearest' });
  }, [idAtivo]);
  const escolher = (m: MunicipioEscolhido) => {
    escolherMunicipio(m);
    setTexto('');
    opts.aoEscolher();
  };
  const teclas = (e: KeyboardEvent) => {
    if (!opts.aberta) {
      // Fechado: ↓ reabre a lista; Esc apaga o que foi digitado.
      if (e.key === 'ArrowDown' && opts.abrir) opts.abrir();
      else if (e.key === 'Escape' && texto) setTexto('');
      else return;
    } else if (e.key === 'ArrowDown') setAtivo((i) => Math.min(i + 1, resultados.length - 1));
    else if (e.key === 'ArrowUp') setAtivo((i) => Math.max(i - 1, 0));
    else if (e.key === 'Enter' && resultados[ativo]) escolher(resultados[ativo]);
    else return;
    e.preventDefault();
  };
  const entrada = {
    type: 'search',
    role: 'combobox',
    'aria-expanded': expandida,
    'aria-controls': lista,
    'aria-autocomplete': 'list',
    'aria-activedescendant': idAtivo,
    'aria-label': 'Pesquisar cidade',
    value: texto,
    onChange: (e: ChangeEvent<HTMLInputElement>) => setTexto(e.target.value),
    onKeyDown: teclas,
    autoComplete: 'off',
    enterKeyHint: 'search',
  } as const;
  return { texto, ativo, setAtivo, indice, erro, lista, resultados, escolher, entrada };
}

type Campo = ReturnType<typeof useCampo>;

/** A lista dos municípios encontrados e, fora dela, o aviso (carregando, erro, nenhum, quantos). */
function Lista(props: { campo: Campo }) {
  const { texto, indice, erro, lista, resultados, ativo, setAtivo, escolher } = props.campo;
  if (!texto.trim()) return null;
  const aviso = erro
    ? 'A lista de cidades não carregou. Tente de novo.'
    : !indice
      ? 'Carregando a lista de cidades…'
      : resultados.length === 0
        ? 'Nenhum município da Amazônia Legal com esse nome.'
        : null;
  return (
    <>
      <p role="status" className={clsx(aviso ? 'px-1 text-xs' : 'sr-only', erro ? 'text-yellow-200' : 'text-ink-300')}>
        {aviso ?? `${resultados.length} ${resultados.length === 1 ? 'cidade encontrada' : 'cidades encontradas'}`}
      </p>
      {resultados.length > 0 && (
        <ul id={lista} role="listbox" aria-label="Cidades encontradas" className="max-h-64 overflow-y-auto rounded-lg border border-ink-700 bg-ink-850">
          {resultados.map((m, i) => (
            <li
              key={m.cd}
              id={`${lista}-${m.cd}`}
              role="option"
              aria-selected={i === ativo}
              // Só o mouse move a opção ativa: no toque, a lista nasce sob o dedo e trocaria a escolha do Enter.
              onPointerEnter={(e) => e.pointerType === 'mouse' && setAtivo(i)}
              onClick={() => escolher(m)}
              className={clsx('flex cursor-pointer items-center gap-2 px-3 py-2 text-sm pointer-coarse:py-3', i === ativo ? 'bg-ink-700 text-white' : 'text-ink-100')}
            >
              <MapPin className="size-3.5 shrink-0 text-ink-400" aria-hidden />
              <span className="min-w-0 flex-1 truncate">{m.nome}</span>
              <span className="text-xs text-ink-400">{m.uf}</span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/** "Usar minha localização" e o aviso do GPS. Deu certo, o município já foi escolhido e a busca fecha. */
function Localizacao(props: { aoEscolher: () => void }) {
  const gps = useBuscaStore((s) => s.gps);
  const { aoEscolher } = props;
  const gpsAntes = useRef(gps);
  useEffect(() => {
    if (gps === 'ok' && gpsAntes.current === 'localizando') aoEscolher();
    gpsAntes.current = gps;
  }, [gps, aoEscolher]);
  return (
    <>
      <button
        type="button"
        onClick={localizar}
        disabled={gps === 'localizando'}
        className="flex min-h-10 w-full items-center gap-2 rounded-lg border border-ink-600 bg-ink-800 px-3 text-sm text-ink-100 hover:bg-ink-700 focus-visible:outline-2 focus-visible:outline-mata-400 disabled:opacity-60 pointer-coarse:min-h-11"
      >
        {gps === 'localizando' ? <LoaderCircle className="size-4 animate-spin text-sky-300" aria-hidden /> : <LocateFixed className="size-4 text-sky-300" aria-hidden />}
        Usar minha localização
      </button>
      {MENSAGEM_DO_GPS[gps] && (
        <p role="status" className={clsx('text-xs leading-snug', gps === 'localizando' ? 'text-ink-300' : 'text-yellow-200')}>
          {MENSAGEM_DO_GPS[gps]}
        </p>
      )}
    </>
  );
}

/** O campo da folha do celular: entrada, lista e localização. */
function CampoDaFolha(props: { aoEscolher: () => void }) {
  const campo = useCampo({ aberta: true, aoEscolher: props.aoEscolher });
  const entrada = useRef<HTMLInputElement>(null);
  useEffect(() => entrada.current?.focus(), []);
  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-ink-400" aria-hidden />
        <input
          ref={entrada}
          {...campo.entrada}
          placeholder="Pesquisar cidade…"
          className="h-10 w-full rounded-lg border border-ink-600 bg-ink-800 pr-3 pl-8 text-sm text-white placeholder:text-ink-400 focus-visible:outline-2 focus-visible:outline-mata-400 pointer-coarse:h-11"
        />
      </div>
      <Lista campo={campo} />
      <Localizacao aoEscolher={props.aoEscolher} />
    </div>
  );
}

/** O ✕ que tira o destaque do município. */
function Limpar(props: { className?: string }) {
  return (
    <button
      type="button"
      onClick={() => useBuscaStore.getState().escolher(null)}
      aria-label="Tirar o destaque do município"
      title="Tirar o destaque do município"
      className={clsx('inline-flex shrink-0 items-center justify-center rounded-lg text-ink-300 hover:bg-ink-700 hover:text-white focus-visible:outline-2 focus-visible:outline-mata-400', props.className)}
    >
      <X className="size-4" aria-hidden />
    </button>
  );
}

/** Algum campo de texto com o foco? (aí "/" é para ser digitado, não um atalho) */
const digitando = (el: Element | null) =>
  el instanceof HTMLElement && (el.isContentEditable || el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT');

/** Desktop: o campo sempre à vista no topo do mapa; a lista e a localização abrem num cartão abaixo dele. */
function BarraDeBusca(props: { className?: string }) {
  const [aberta, setAberta] = useState(false);
  const [focado, setFocado] = useState(false);
  const municipio = useBuscaStore((s) => s.municipio);
  const caixa = useRef<HTMLDivElement>(null);
  const entrada = useRef<HTMLInputElement>(null);
  const descricao = useId();
  // Escolhido ou fechado com Esc, a lista fecha e o foco fica no campo.
  const fechar = useCallback(() => setAberta(false), []);
  const abrir = useCallback(() => setAberta(true), []);
  const campo = useCampo({ aberta, abrir, aoEscolher: fechar });
  useModalLayer(aberta, { onEscape: fechar, modal: false, ref: caixa });
  // "/" (fora de um campo de texto) ou Ctrl/⌘+K levam à busca.
  useEffect(() => {
    const atalho = (e: globalThis.KeyboardEvent) => {
      const barra = e.key === '/' && !e.ctrlKey && !e.metaKey && !e.altKey && !digitando(document.activeElement);
      const k = e.key.toLowerCase() === 'k' && (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey;
      if (!barra && !k) return;
      e.preventDefault();
      entrada.current?.focus();
    };
    window.addEventListener('keydown', atalho);
    return () => window.removeEventListener('keydown', atalho);
  }, []);
  // O foco saiu da caixa (Tab, clique no mapa): a lista fecha.
  const saiu = (e: FocusEvent) => {
    if (!caixa.current?.contains(e.relatedTarget as Node | null)) {
      setAberta(false);
      setFocado(false);
    }
  };
  // Escolhido, o nome fica no campo (por cima, sem virar texto digitado) até a próxima busca; com a
  // lista aberta, ele vira o texto de exemplo.
  const mostraEscolhido = municipio && !campo.texto && !aberta;

  return (
    <div ref={caixa} className={clsx('relative', props.className)} onBlur={saiu}>
      <div
        className={clsx(
          'flex h-9 items-center rounded-xl border bg-ink-950/90 text-ink-100 shadow-lg transition-colors pointer-coarse:h-11',
          'border-ink-500 hover:border-ink-400 focus-within:border-mata-400 focus-within:ring-2 focus-within:ring-mata-400/40',
        )}
        data-botao-busca=""
      >
        {municipio ? <MapPin className="ml-3 size-4 shrink-0 text-white" aria-hidden /> : <Search className="ml-3 size-4 shrink-0 text-ink-200" aria-hidden />}
        <div className="relative h-full min-w-0 flex-1">
          <input
            ref={entrada}
            {...campo.entrada}
            aria-describedby={municipio ? descricao : undefined}
            onFocus={() => {
              setFocado(true);
              setAberta(true);
            }}
            onClick={abrir}
            onChange={(e) => {
              campo.entrada.onChange(e);
              setAberta(true);
            }}
            placeholder={mostraEscolhido ? '' : municipio ? rotulo(municipio) : 'Pesquisar cidade…'}
            className="h-full w-full bg-transparent px-2 text-sm text-white placeholder:text-ink-300 focus:outline-none [&::-webkit-search-cancel-button]:hidden"
          />
          {mostraEscolhido && (
            <span className="pointer-events-none absolute inset-y-0 left-2 flex max-w-[calc(100%-8px)] items-center text-sm font-medium text-white">
              <span className="truncate" data-busca-escolhido="">
                {rotulo(municipio)}
              </span>
            </span>
          )}
          {municipio && (
            <span id={descricao} className="sr-only">
              Cidade destacada no mapa: {rotulo(municipio)}
            </span>
          )}
        </div>
        {municipio ? (
          <Limpar className="mr-1 size-7 pointer-coarse:size-10" />
        ) : (
          !focado && (
            <kbd className="mr-2.5 hidden rounded border border-ink-600 px-1.5 font-sans text-[10px] text-ink-300 xl:inline" title="Atalho: / ou Ctrl+K">
              /
            </kbd>
          )
        )}
      </div>
      {aberta && (
        // O mouse aqui não tira o foco do campo (senão a lista fecharia antes do clique).
        <div className="absolute top-full right-0 left-0 z-30 mt-2 space-y-2 rounded-xl border border-ink-600 bg-ink-900 p-2.5 shadow-2xl" onMouseDown={(e) => e.preventDefault()}>
          {!campo.texto.trim() && <p className="px-1 text-xs text-ink-300">Digite o nome de uma cidade da Amazônia Legal, ou:</p>}
          <Lista campo={campo} />
          <Localizacao aoEscolher={fechar} />
        </div>
      )}
    </div>
  );
}

export function BuscaMunicipio(props: { variante: 'desktop' | 'celular'; className?: string }) {
  return props.variante === 'desktop' ? <BarraDeBusca className={props.className} /> : <BotaoDoCelular className={props.className} />;
}

/** Celular: "Pesquisar" (só a lupa abaixo de 360 px) abre a busca numa folha no topo da tela. */
function BotaoDoCelular(props: { className?: string }) {
  const [aberta, setAberta] = useState(false);
  const municipio = useBuscaStore((s) => s.municipio);
  const caixa = useRef<HTMLDivElement>(null);
  const fechar = useCallback(() => setAberta(false), []);
  useModalLayer(aberta, { onEscape: fechar, modal: true, ref: caixa });

  return (
    <>
      <button
        type="button"
        onClick={() => setAberta(true)}
        aria-label={municipio ? `${rotulo(municipio)}: pesquisar outra cidade` : 'Pesquisar cidade'}
        title="Pesquisar cidade"
        data-botao-busca=""
        className={clsx(
          CHIP,
          // Contêiner: sem largura para a palavra (relógio mais largo, "NOAA atrasada"), fica só a lupa.
          '@container inline-flex h-11 min-w-0 items-center justify-center gap-1.5 rounded-xl px-2.5',
          'max-[359px]:w-11 max-[359px]:flex-none max-[359px]:px-0',
          props.className,
        )}
      >
        {municipio ? <MapPin className="size-[18px] shrink-0 text-white" aria-hidden /> : <Search className="size-[18px] shrink-0 text-ink-200" aria-hidden />}
        <span className={clsx('min-w-0 flex-1 truncate text-left text-sm max-[359px]:hidden @max-[5.5rem]:hidden', municipio ? 'font-medium text-white' : 'text-ink-200')}>
          {/* Só o nome: no espaço do topo, a sigla do estado seria o que corta. */}
          {municipio ? municipio.nome || rotulo(municipio) : 'Pesquisar'}
        </span>
      </button>
      {aberta &&
        // Portal para o <body>: a folha é modal, e o `#root` (onde fica o botão) fica `inert` com ela
        // aberta (ui/modalStack) — dentro dele, o campo não receberia nem o foco nem o toque.
        createPortal(
          <div ref={caixa}>
            <div className="fixed inset-0 z-40 bg-ink-950/40" onClick={fechar} aria-hidden />
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Pesquisar cidade"
              className="fixed inset-x-0 top-0 z-50 space-y-2 rounded-b-2xl border-b border-ink-600 bg-ink-900 px-3 pt-[calc(env(safe-area-inset-top)+12px)] pb-3 shadow-2xl"
            >
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-white">Pesquisar cidade</h2>
                <button
                  type="button"
                  onClick={fechar}
                  aria-label="Fechar a busca"
                  className="inline-flex size-11 items-center justify-center rounded-lg text-ink-300 hover:bg-ink-800 focus-visible:outline-2 focus-visible:outline-mata-400"
                >
                  <X className="size-5" aria-hidden />
                </button>
              </div>
              {municipio && (
                <div className="flex items-center gap-2 rounded-lg border border-ink-700 bg-ink-850 py-1 pr-1 pl-3 text-sm text-white" data-busca-escolhido="">
                  <MapPin className="size-4 shrink-0 text-white" aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{rotulo(municipio)}</span>
                  <Limpar className="size-11" />
                </div>
              )}
              <CampoDaFolha aoEscolher={fechar} />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
